import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, InputAdornment, Paper, Skeleton, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ActivityWindowSelector from './ActivityWindowSelector';
import { getLocale } from '../DateRangeSelector';
import DataBuildingBanner from './DataBuildingBanner';
import EmptyState from './EmptyState';
import ListToolbar from './ListToolbar';
import FilterFields from './FilterFields';
import ProductList from './ProductList';
import SizeKey from './SizeKey';
import ViewChips from './ViewChips';
import ViewDescription from './ViewDescription';
import useBuyerInsightsFilters from './useBuyerInsightsFilters';
import useContentWidth, { isWideWidth } from './useContentWidth';
import {
  DEFAULT_TIMING_GROUP,
  N_DEFAULT,
  SOLD_OUT_AT_IGNORED_VIEWS,
  SOLD_OUT_AT_NOTE,
  TIMING_GROUPS,
  visibleViews,
} from './constants';
import { dashboardAPI } from '../../../services/dashboard-api';
import { track } from '../../../utils/analytics';
import { VIEW_SIDE, buildProductsParams, filterParams, fmtInt, sortOptions } from '../../../utils/buyerInsightsFormat';

/**
 * Products tab (spec §2). State lives here; dates are YYYY-MM-DD strings; every number on the tab
 * comes from the latest /buyer-insights/products response so chips, rows and count never disagree.
 */
const ProductsTab = ({ myRetailers = [], myRetailerId = '', onRetailerChange, initial = {}, onFiltersChange }) => {
  const rootRef = useRef(null);
  const width = useContentWidth(rootRef);
  const isWide = isWideWidth(width);

  const f = useBuyerInsightsFilters({ tab: 'products', myRetailerId, initial, onFiltersChange });
  const {
    meta, window, setWindow, brand, category, status, live, sale, soldOutAt, offRange, bands, q, setQ, debouncedQ, hasFilters, filterChanged,
  } = f;
  const [view, setView] = useState(() => initial.view || 'all');
  const [n, setN] = useState(N_DEFAULT);
  const [timingGroup, setTimingGroup] = useState(() => initial.group || DEFAULT_TIMING_GROUP);
  const [sort, setSort] = useState('sent_clicks');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [expandAll, setExpandAll] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const reqSeq = useRef(0);
  const abortRef = useRef(null);

  const side = data?.side || VIEW_SIDE[view];
  const params = useMemo(
    () =>
      buildProductsParams({
        window, myRetailerId, view, brand, category, status, live, sale, soldOutAt, offRange, bands, q: debouncedQ, n, sort, group: timingGroup,
      }),
    [window, myRetailerId, view, brand, category, status, live, sale, soldOutAt, offRange, bands, debouncedQ, n, sort, timingGroup]
  );
  const paramsKey = JSON.stringify(params);
  // The size table follows every filter of the list (sold-out-at spec §3), so its sizes add up to the row.
  const detailFilters = useMemo(
    () => filterParams({ window, myRetailerId, brand, category, status, live, sale, soldOutAt, offRange, bands, q: debouncedQ }),
    [window, myRetailerId, brand, category, status, live, sale, soldOutAt, offRange, bands, debouncedQ]
  );
  // Views that are not sell-outs ignore Sold out at: the control greys out and says so (spec §3, §4).
  const soldOutAtNote = SOLD_OUT_AT_IGNORED_VIEWS.includes(view) ? SOLD_OUT_AT_NOTE : '';
  const onBreakdownSelect = ({ soldOutAt: soa, offRange: range, label }) => {
    f.setSoldOutAt(soa);
    f.setOffRange(range);
    filterChanged('sold_out_at', label, { source: 'breakdown' });
  };

  // Page 1 on every params change; stale responses are discarded.
  useEffect(() => {
    reqSeq.current += 1;
    const mine = reqSeq.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = controller;
    setLoading(true);
    setError('');
    dashboardAPI
      .getBuyerInsightsProducts({ ...params, page: 1 }, { signal: controller?.signal })
      .then((res) => {
        if (mine !== reqSeq.current) return;
        if (res?.success) {
          setData(res.data);
          setExpandAll(false);
          setExpandedIds(new Set());
        } else {
          setError(res?.error || 'Could not load products');
        }
      })
      .catch((e) => {
        if (mine !== reqSeq.current || e?.name === 'AbortError') return;
        setError(e.message || 'Could not load products');
      })
      .finally(() => {
        if (mine === reqSeq.current) setLoading(false);
      });
  }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const hasMore = Boolean(data) && rows.length < total;

  const showMore = useCallback(async () => {
    if (!data || loadingMore) return;
    const mine = reqSeq.current;
    setLoadingMore(true);
    try {
      const res = await dashboardAPI.getBuyerInsightsProducts({ ...params, page: data.page + 1 });
      if (mine !== reqSeq.current || !res?.success) return;
      setData((prev) => ({ ...res.data, rows: [...prev.rows, ...res.data.rows] }));
    } catch (e) {
      setError(e.message || 'Could not load more');
    } finally {
      setLoadingMore(false);
    }
  }, [data, loadingMore, params]);

  // Expansion semantics (spec §2.5).
  // Expand all would open up to 50 timelines at once in Discount timing; rows still open one at a time.
  const expandAllDisabled = view === 'discount_timing';
  const isExpanded = (id) => expandAll || expandedIds.has(id);
  const allExpanded = expandAll || (rows.length > 0 && rows.every((r) => expandedIds.has(r.product_id)));
  const onToggleRow = (id) => {
    if (expandAll) {
      setExpandAll(false);
      setExpandedIds(new Set());
      return;
    }
    const opening = !expandedIds.has(id);
    setExpandedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
    if (opening) track('Buyer Insights Row Expanded', { tab: 'products', view, all: false });
  };
  const onToggleAll = () => {
    if (allExpanded) {
      setExpandAll(false);
      setExpandedIds(new Set());
    } else {
      setExpandAll(true);
      setExpandedIds(new Set());
      track('Buyer Insights Row Expanded', { tab: 'products', view, all: true });
    }
  };

  const onSort = (column) => {
    setSort(column);
    track('Buyer Insights Sorted', { tab: 'products', view, column, layout: isWide ? 'wide' : 'narrow' });
  };
  const sizeInsertionEnabled = Boolean(meta?.size_insertion_enabled);
  const views = visibleViews(sizeInsertionEnabled);
  // A store switch can land on a retailer without the flag: fall back to All products.
  useEffect(() => {
    if (meta && view === 'never_bought' && !sizeInsertionEnabled) setView('all');
  }, [meta, view, sizeInsertionEnabled]);

  const onSelectView = (key, count) => {
    setView(key);
    track('Buyer Insights View Selected', { tab: 'products', view: key, count, source: 'chip' });
  };

  const timingCounts = data?.groups ? Object.fromEntries(data.groups.map((g) => [g.key, g.count])) : null;
  const onSelectTimingGroup = (key, count) => {
    setTimingGroup(key);
    track('Buyer Insights Filter Changed', { tab: 'products', filter: 'timing_group', value: key, count });
  };

  const emptyState = (
    <EmptyState
      view={view}
      n={n}
      q={debouncedQ}
      hasFilters={hasFilters}
      group={view === 'discount_timing' && data?.counts?.discount_timing ? timingGroup : ''}
      sale={sale}
      soldOutAt={soldOutAt}
      signature={paramsKey}
    />
  );

  const listProps = {
    side,
    view,
    isWide,
    loading,
    loadingMore,
    hasMore,
    onShowMore: showMore,
    sort,
    onSort,
    isExpanded,
    onToggleRow,
    expandDisabled: false,
    window,
    n,
    myRetailerId,
    filters: detailFilters,
    emptyState,
  };

  return (
    <Box ref={rootRef} sx={{ minWidth: 0 }}>
      <DataBuildingBanner firstActivatedAt={meta?.first_activated_at} />

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getLocale()}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ActivityWindowSelector value={window} onChange={f.onWindowChange} bounds={f.bounds} isWide={isWide} />
            <FilterFields
              f={f}
              isWide={isWide}
              myRetailers={myRetailers}
              myRetailerId={myRetailerId}
              onRetailerChange={onRetailerChange}
              soldOutAtNote={soldOutAtNote}
            />

            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                View
              </Typography>
              <ViewChips views={views} counts={data?.counts} active={view} onSelect={onSelectView} isWide={isWide} loading={loading} />
              <Box sx={{ mt: 1 }}>
                <ViewDescription
                  view={view}
                  n={n}
                  onNChange={(v) => {
                    setN(v);
                    filterChanged('n', v);
                  }}
                  breakdown={data?.sold_out_at_breakdown}
                  onBreakdownSelect={onBreakdownSelect}
                />
              </Box>
              {view === 'discount_timing' && (
                <Box sx={{ mt: 1.5 }}>
                  <ViewChips
                    views={TIMING_GROUPS}
                    counts={timingCounts}
                    active={timingGroup}
                    onSelect={onSelectTimingGroup}
                    isWide={isWide}
                    loading={loading}
                    ariaLabel="Discount timing group"
                    secondary
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
              <TextField
                size="small"
                placeholder="Search products"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                inputProps={{ 'aria-label': 'Search products' }}
                sx={{ width: isWide ? 320 : '100%', '& .MuiInputBase-root': { minHeight: isWide ? 40 : 44 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {data ? `${fmtInt(total)} ${total === 1 ? 'product' : 'products'}` : <Skeleton width={90} />}
              </Typography>
            </Box>
          </Box>
        </LocalizationProvider>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setWindow((w) => ({ ...w }))}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {view === 'all' && <SizeKey end={window.end} />}

      <ListToolbar
        side={side}
        isWide={isWide}
        sort={sort}
        onSort={onSort}
        options={sortOptions(side, { revenue: view !== 'only_you' })}
        allExpanded={allExpanded}
        onToggleAll={onToggleAll}
        expandDisabled={expandAllDisabled || rows.length === 0}
        expandAllNote={expandAllDisabled ? 'Open timelines one product at a time' : ''}
        exportProps={{ params, view, disabled: loading || total === 0 }}
      />

      <ProductList rows={rows} {...listProps} />
    </Box>
  );
};

export default ProductsTab;
